import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (data.valid) { setEmail(data.email); setState("valid"); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Account security</div>
      <h1 className="font-display text-5xl text-darkRoast mb-10">Reset password.</h1>

      {state === "checking" && (
        <p className="text-earth" data-testid="reset-checking">Verifying your link…</p>
      )}

      {state === "invalid" && (
        <div className="text-center py-6" data-testid="reset-invalid">
          <XCircle className="w-12 h-12 text-terracotta mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-earth mb-6">This reset link is invalid or has expired.</p>
          <Link to="/login" className="inline-block bg-darkRoast text-cream rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors" data-testid="reset-back-login">
            Back to sign in
          </Link>
        </div>
      )}

      {state === "valid" && (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <p className="text-sm text-earth">Setting a new password for <span className="text-darkRoast">{email}</span>.</p>
          <div>
            <label className="text-xs tracking-wider-2 uppercase text-earth">New password</label>
            <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="reset-pw" />
          </div>
          <div>
            <label className="text-xs tracking-wider-2 uppercase text-earth">Confirm password</label>
            <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="reset-pw2" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-darkRoast text-cream py-3.5 rounded-full text-sm tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60" data-testid="reset-submit">
            {loading ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}

      {state === "done" && (
        <div className="text-center py-6" data-testid="reset-done">
          <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-earth mb-6">Your password has been updated.</p>
          <button onClick={() => navigate("/login")} className="bg-darkRoast text-cream rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors" data-testid="reset-go-login">
            Sign in
          </button>
        </div>
      )}
    </div>
  );
}
